import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgModel} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {finalize, map, switchMap, tap} from "rxjs/operators";
import {Observable, of, Subscription} from "rxjs";
import {Location} from "@angular/common";
import {
  AccountProductsComponent,
  ProductsListDataAdapter
} from "../../components/account-products/account-products.component";
import {V3School, V3Student} from '../../model/v3/schools';
import {SchoolV3RestService} from '../../services/rest/v3/school-rest.service';
import {TransactionsV3RestService} from '../../services/rest/v3/transactions-rest.service';
import {WorkflowsRestService} from "../../services/rest/v3/workflows-rest.service";
import {
  ContactType,
  PersonInvitation,
  V3Address,
  V3ContactEntry,
  V3PersonalSensitiveDetails,
  V3PersonDetails,
  V3PersonInvitationDetails,
  V3Settings
} from "../../model/v3/people";
import {V3FileItem} from "../../model/v3/file";
import {ProductRef} from "../../model/v3/transactions";

export class InvitationComponentData {
  constructor(public action: InvitationComponentAction, public role: InvitationRoleType) {
  }
}

export enum InvitationComponentAction {
  ActionInvite
}
export class InvitationRoleType {
  constructor(public hasParentView: boolean, public resourceName: string) {
  }

  static Student = new InvitationRoleType(true, "student");
  static Teacher = new InvitationRoleType(true, "teacher");
  static Manager = new InvitationRoleType(false, "manager");
}

@Component({
    selector: 'app-manager-person-invitation-form-page',
    templateUrl: './manager-person-invitation-form-page.component.html',
    styleUrls: ['./manager-person-invitation-form-page.component.css'],
    standalone: false
})
export class ManagerPersonInvitationFormPageComponent implements OnInit, OnDestroy {
  schoolId: number;
  private school: V3School;
  invitation: PersonInvitation<V3PersonInvitationDetails>;
  private readOnly = false;
  private readonly role: InvitationRoleType;
  private finished = false;
  private errors = false;
  @ViewChild("studentProducts")
  productsComponent: AccountProductsComponent;
  @ViewChild("email")
  emailModel: NgModel;
  adapter: ProductsListDataAdapter;
  personDetails: V3PersonDetails
  sensitiveDetails: V3PersonalSensitiveDetails
  settings: V3Settings
  address: V3Address
  photo: V3FileItem
  contacts: V3ContactEntry[];

  //this field is used only for the student form
  createdStudentId: number;
  private dataSubscription: Subscription;

  constructor(
    route: ActivatedRoute,
    private schoolV3Rest: SchoolV3RestService,
    private workflowsRest: WorkflowsRestService,
    private _location: Location,
    private transactionsRest: TransactionsV3RestService
  ) {
    this.dataSubscription = route.parent.paramMap
      .pipe(
        map( params => Number(params.get('schoolId'))),
        tap<number>( schoolId => this.schoolId = schoolId),
        tap( _ => this.clearForm())
      ).subscribe( _ => this.reload());
    const data = route.snapshot.data as InvitationComponentData;
    const self = this
    this.role = data.role;
    this.adapter = {
      listProducts: () => {
        if (!self.createdStudentId || !self.schoolId) return of([]);
        return this.transactionsRest.getStudentProducts(this.schoolId, this.createdStudentId)
      },
      syncProducts: (products: ProductRef[]) =>
        transactionsRest.syncStudentsProducts(this.schoolId, this.createdStudentId, products),
    }
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe()
  }

  clearForm() {
    this.invitation = {} as PersonInvitation<V3PersonInvitationDetails>;
    this.invitation.email = null
    this.finished = false;
    this.errors = false;
    this.personDetails = {} as V3PersonDetails;
    const contact = {} as V3ContactEntry;
    contact.entryType = ContactType.PHONE;
    this.address = {} as V3Address;
    this.contacts = [contact];
    this.settings = {} as V3Settings;
  }

  isSchoolReady() {
    return this.school;
  }

  private reload() {
    if (!this.schoolId) return;
    this.loadSchool().subscribe();
  }

  private loadSchool() {
    return this.schoolV3Rest.getSchool(this.schoolId).pipe(
      tap(school => this.storeSchool(school))
    );
  }

  private storeSchool(school: V3School) {
    this.school = school;
  }

  getType() {
    return this.role.resourceName;
  }

  isReadOnly() {
    return this.readOnly;
  }

  onSave() {
    this.readOnly = true;
    this.finished = false;
    this.errors = false;

    let actionOrder = this.dispatchUpdateAction();

    if (this.role === InvitationRoleType.Student) {
      actionOrder = (actionOrder as Observable<V3Student>).pipe(
        tap( student => this.createdStudentId = student.id),
        switchMap(_ => this.productsComponent.postOrder()),
        finalize( () => {
          this.createdStudentId = null;
          this.readOnly = false;
        })
      )
    } else {
      actionOrder = (actionOrder as Observable<any>).pipe(
        finalize(() => this.readOnly = false)
      );
    }

    (actionOrder as Observable<any>).pipe(
    ).subscribe(
      {
        next: _ => {
          this.finished = true;
          this.invitation = null;
        },
        error: _ => {
          this.errors = true;
        }
      }
    )
  }

  hasParent() {
    return this.role.hasParentView;
  }

  isFinished() {
    return this.finished;
  }

  hasErrors() {
    return this.errors;
  }

  private dispatchUpdateAction() {
      switch (this.role) {
        case InvitationRoleType.Student:
          return this.workflowsRest.inviteStudent(this.schoolId, this.invitation);
        case InvitationRoleType.Manager:
          return this.workflowsRest.inviteManager(this.schoolId, this.invitation);
        case InvitationRoleType.Teacher:
          return this.workflowsRest.inviteTeacher(this.schoolId, this.invitation);
      }
  }

  back() {
    this._location.back();
  }

  doSave(invitationDetails: V3PersonInvitationDetails) {
    if (!this.invitation.email || this.emailModel?.invalid) return;
    this.invitation.details = invitationDetails;
    this.onSave();
  }

  getFormRole() {
    switch (this.role) {
      case InvitationRoleType.Student:
        return 'student';
      case InvitationRoleType.Manager:
        return 'manager';
      case InvitationRoleType.Teacher:
        return 'teacher';
      default:
        return null;
    }
  }

  productListType(): "teacher" | "student" {
    const formRole = this.getFormRole();
    if (!formRole || formRole === 'manager') return null;
    return formRole;
  }
}
